import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckSquare } from "lucide-react";
import { createQuiz, listQuizzes, type QuizListItem } from "../api/quizzes";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { EmptyState } from "../components/EmptyState";

function canEditQuizzes(roles: string[]): boolean {
  return roles.some((r) => r === "super_admin" || r === "admin" || r === "content_manager");
}

export function QuizListPage() {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [items, setItems] = useState<QuizListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listQuizzes(debouncedQ || undefined);
      setItems(r.items);
    } catch (err) {
      setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setLoading(false);
    }
  }, [debouncedQ]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!user) return null;
  const canEdit = canEditQuizzes(user.roles);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    try {
      const qz = await createQuiz({ title: newTitle.trim() });
      navigate(`/quizzes/${qz.id}`);
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Тесты</h1>
          <p className="text-sm text-slate-500">Всего: {items.length}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="btn-secondary"
          >
            ← На главную
          </Link>
          {canEdit && (
            <button
              type="button"
              onClick={() => setCreateOpen((s) => !s)}
              className="btn-primary"
            >
              {createOpen ? "Отмена" : "+ Новый тест"}
            </button>
          )}
        </div>
      </header>

      {createOpen && (
        <form
          onSubmit={handleCreate}
          className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-3"
        >
          <input
            type="text"
            required
            placeholder="Название теста"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            className="min-w-[260px] flex-1 rounded border border-slate-300 px-3 py-2 text-sm"
            autoFocus
          />
          <button
            type="submit"
            disabled={creating}
            className="btn-primary"
          >
            {creating ? "…" : "Создать и открыть"}
          </button>
        </form>
      )}

      <div className="mb-4 rounded-lg bg-white p-3 shadow-sm">
        <input
          type="search"
          placeholder="Поиск по названию…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
        />
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="py-12 text-center text-ink-muted">Загрузка…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={CheckSquare}
          title="Тестов пока нет"
          description={canEdit ? "Создай первый, чтобы проверять знания учеников." : "Контент-менеджер их подготовит."}
        />
      ) : (
        <ul className="space-y-2">
          {items.map((qz) => (
            <li key={qz.id}>
              <Link
                to={`/quizzes/${qz.id}`}
                className="flex items-center justify-between rounded-lg bg-white p-3 shadow-sm transition hover:shadow-md"
              >
                <div>
                  <div className="font-medium text-slate-900">{qz.title}</div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {qz.questions_count} вопросов · проходной {qz.passing_score_percent}%
                  </div>
                </div>
                <span className="badge badge-neutral">
                  {qz.scope}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
